// TUT-450 — as notificações: o sino, catálogo, regras, entregas e canais (os grupos moram em `grupos.ts`).
import { demoApi } from "../demo-data";
import type {
  CreateNotificationRuleInput,
  NotificationCatalogEntry,
  NotificationChannelsResponse,
  NotificationDeliveryListQuery,
  NotificationDeliveryListResponse,
  NotificationListResponse,
  NotificationRule,
  UpdateNotificationRuleInput,
} from "../types";
import type { AdiarAte, CaixaDoSino } from "../notificacoes/caixa";
import { comFatia, request } from "./infra";
import type { FatiaPedida } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDasNotificacoes = {
  notifications(
    search = "",
    unreadOnly = false,
    fatia: FatiaPedida = {},
    modulo = "",
    // TUT-586b — a caixa: os adiados ou os arquivados (sem ela, a lista de todo dia).
    caixa?: CaixaDoSino,
  ): Promise<NotificationListResponse> {
    if (useDemoApi) return demoApi.notifications(search, unreadOnly, fatia, modulo, caixa);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (unreadOnly) params.set("unreadOnly", "true");
    if (modulo) params.set("modulo", modulo);
    if (caixa) params.set("caixa", caixa);
    return request<NotificationListResponse>(
      `/notifications${comFatia(params, fatia)}`,
    );
  },
  markNotificationRead(id: string): Promise<{ ok: boolean }> {
    if (useDemoApi) return demoApi.markNotificationRead(id);
    return request<{ ok: boolean }>(`/notifications/${id}/read`, {
      method: "PATCH",
      body: JSON.stringify({}),
    });
  },
  /** TUT-517 — "Marcar todas como lidas", de uma vez. */
  markAllNotificationsRead(): Promise<{ updated: number }> {
    if (useDemoApi) return demoApi.markAllNotificationsRead();
    return request<{ updated: number }>("/notifications/read-all", {
      method: "PATCH",
      body: JSON.stringify({}),
    });
  },
  /** TUT-586b — adia (a API calcula a volta no fuso do escritório) e devolve a hora da volta. */
  snoozeNotification(id: string, ate: AdiarAte): Promise<{ snoozedUntil: string }> {
    if (useDemoApi) return demoApi.snoozeNotification(id, ate);
    return request<{ snoozedUntil: string }>(`/notifications/${id}/snooze`, {
      method: "PATCH",
      body: JSON.stringify({ ate }),
    });
  },
  /** TUT-586b — desfaz o adiamento (Reativar e o "Desfazer" do aviso). */
  unsnoozeNotification(id: string): Promise<{ ok: boolean }> {
    if (useDemoApi) return demoApi.unsnoozeNotification(id);
    return request<{ ok: boolean }>(`/notifications/${id}/unsnooze`, { method: "PATCH", body: JSON.stringify({}) });
  },
  archiveNotification(id: string): Promise<{ ok: boolean }> {
    if (useDemoApi) return demoApi.archiveNotification(id);
    return request<{ ok: boolean }>(`/notifications/${id}/archive`, { method: "PATCH", body: JSON.stringify({}) });
  },
  unarchiveNotification(id: string): Promise<{ ok: boolean }> {
    if (useDemoApi) return demoApi.unarchiveNotification(id);
    return request<{ ok: boolean }>(`/notifications/${id}/unarchive`, { method: "PATCH", body: JSON.stringify({}) });
  },
  /** TUT-517 — devolve o aviso ao selo. */
  markNotificationUnread(id: string): Promise<{ ok: boolean }> {
    if (useDemoApi) return demoApi.markNotificationUnread(id);
    return request<{ ok: boolean }>(`/notifications/${id}/unread`, {
      method: "PATCH",
      body: JSON.stringify({}),
    });
  },
  notificationCatalog(): Promise<{ items: NotificationCatalogEntry[] }> {
    if (useDemoApi) return demoApi.notificationCatalog();
    return request<{ items: NotificationCatalogEntry[] }>(
      "/notifications/catalog",
    );
  },
  notificationRules(): Promise<{ items: NotificationRule[] }> {
    if (useDemoApi) return demoApi.notificationRules();
    return request<{ items: NotificationRule[] }>("/notification-rules");
  },
  createNotificationRule(
    input: CreateNotificationRuleInput,
  ): Promise<NotificationRule> {
    if (useDemoApi) return demoApi.createNotificationRule(input);
    return request<NotificationRule>("/notification-rules", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateNotificationRule(
    id: string,
    input: UpdateNotificationRuleInput,
  ): Promise<NotificationRule> {
    if (useDemoApi) return demoApi.updateNotificationRule(id, input);
    return request<NotificationRule>(`/notification-rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  /** TUT-484 — remove a regra (a API a marca; a padrão removida não volta). */
  removeNotificationRule(id: string): Promise<{ padrao: boolean }> {
    if (useDemoApi) return demoApi.removeNotificationRule(id);
    return request<{ padrao: boolean }>(`/notification-rules/${id}`, {
      method: "DELETE",
    });
  },
  testNotificationRule(
    id: string,
  ): Promise<{ internal: number; sent: number; failed: number }> {
    if (useDemoApi) return demoApi.testNotificationRule(id);
    return request<{ internal: number; sent: number; failed: number }>(
      `/notification-rules/${id}/test`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },
  retryNotificationDelivery(id: string): Promise<{ delivered: boolean }> {
    if (useDemoApi) return demoApi.retryNotificationDelivery(id);
    return request<{ delivered: boolean }>(
      `/notification-deliveries/${id}/retry`,
      { method: "POST", body: JSON.stringify({}) },
    );
  },
  notificationDeliveries(
    query: NotificationDeliveryListQuery = {},
  ): Promise<NotificationDeliveryListResponse> {
    if (useDemoApi) return demoApi.notificationDeliveries(query);
    const params = new URLSearchParams({
      page: String(query.page ?? 1),
      pageSize: String(query.pageSize ?? 20),
    });
    if (query.search) params.set("search", query.search);
    // Multi-status vai repetido na query (`?status=a&status=b`), igual à URL.
    for (const status of query.statuses ?? []) params.append("status", status);
    // TUT-204c: só escreve o que veio — sem `sort`, vale o padrão da lista.
    if (query.sort) params.set("sort", query.sort);
    if (query.dir) params.set("dir", query.dir);
    return request<NotificationDeliveryListResponse>(
      `/notification-deliveries?${params}`,
    );
  },
  notificationChannels(): Promise<NotificationChannelsResponse> {
    if (useDemoApi) return demoApi.notificationChannels();
    return request<NotificationChannelsResponse>("/notification-channels");
  },
};
